import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Plus, Trash2, Edit, Loader2, X, Save, Search, Upload, Check } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_FORM = {
  exam_id: 'ssc-cgl',
  exam_name: 'SSC CGL',
  tier: 'Tier 1',
  year: new Date().getFullYear(),
  shift: '',
  exam_date: '',
  language: 'English',
  pdf_url: '',
  has_answer_key: true,
  is_published: true,
  notes: ''
};

const EXAM_PRESETS = [
  { id: 'ssc-cgl', name: 'SSC CGL' },
  { id: 'ssc-chsl', name: 'SSC CHSL' },
  { id: 'ssc-mts', name: 'SSC MTS' },
  { id: 'ssc-cpo', name: 'SSC CPO' },
  { id: 'ssc-gd', name: 'SSC GD' },
  { id: 'ssc-steno', name: 'SSC Stenographer' },
  { id: 'ibps-po', name: 'IBPS PO' },
  { id: 'ibps-clerk', name: 'IBPS Clerk' },
  { id: 'sbi-po', name: 'SBI PO' },
  { id: 'sbi-clerk', name: 'SBI Clerk' },
  { id: 'rbi-grade-b', name: 'RBI Grade B' },
  { id: 'nabard-grade-a', name: 'NABARD Grade A' }
];

const ManagePYQ = () => {
  const [pyqs, setPyqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadInfo, setUploadInfo] = useState(null);
  const fileInputRef = useRef(null);

  const token = () => localStorage.getItem('authToken');

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      alert('Only PDF files are allowed');
      e.target.value = '';
      return;
    }
    setUploading(true);
    setUploadInfo(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_URL}/api/pyq/upload-pdf`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}` },
        body: fd
      });
      const data = await res.json();
      if (!res.ok) {
        // Session expired → redirect to login so user doesn't lose work permanently
        if (res.status === 401 || res.status === 403 || /expired|invalid.*token/i.test(data.detail || '')) {
          const proceed = window.confirm(
            'Your admin session has expired. Click OK to log in again — you will be brought back here.'
          );
          if (proceed) {
            localStorage.removeItem('authToken');
            window.location.href = '/admin/login?redirect=/admin/pyq';
          }
          return;
        }
        throw new Error(data.detail || 'Upload failed');
      }
      // Build absolute URL so it stays valid even if hostname changes later
      const absoluteUrl = data.url.startsWith('http') ? data.url : `${API_URL}${data.url}`;
      setForm(f => ({ ...f, pdf_url: absoluteUrl }));
      setUploadInfo({
        name: data.original_name,
        size: (data.size_bytes / 1024 / 1024).toFixed(2)
      });
    } catch (err) {
      alert(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/pyq/admin`, {
        headers: { Authorization: `Bearer ${token()}` }
      });
      const data = res.ok ? await res.json() : [];
      setPyqs(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    if (!search) return pyqs;
    const q = search.toLowerCase();
    return pyqs.filter(p =>
      `${p.exam_name} ${p.tier} ${p.year} ${p.exam_date} ${p.shift} ${p.language}`.toLowerCase().includes(q)
    );
  }, [pyqs, search]);

  const startCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setUploadInfo(null);
    setShowForm(true);
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setForm({
      exam_id: p.exam_id,
      exam_name: p.exam_name,
      tier: p.tier,
      year: p.year,
      shift: p.shift || '',
      exam_date: p.exam_date || '',
      language: p.language,
      pdf_url: p.pdf_url,
      has_answer_key: p.has_answer_key,
      is_published: p.is_published,
      notes: p.notes || ''
    });
    setUploadInfo(null);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.pdf_url) {
      alert('Please upload a PDF file before saving.');
      return;
    }
    setSubmitting(true);
    try {
      const url = editingId
        ? `${API_URL}/api/pyq/${editingId}`
        : `${API_URL}/api/pyq`;
      const res = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token()}`
        },
        body: JSON.stringify(form)
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Save failed');
      }
      setShowForm(false);
      setEditingId(null);
      fetchAll();
    } catch (err) {
      alert(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this PYQ entry?')) return;
    try {
      await fetch(`${API_URL}/api/pyq/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token()}` }
      });
      fetchAll();
    } catch (e) {
      console.error(e);
    }
  };

  const togglePublish = async (p) => {
    await fetch(`${API_URL}/api/pyq/${p.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token()}`
      },
      body: JSON.stringify({ is_published: !p.is_published })
    });
    fetchAll();
  };

  return (
    <div className="container mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Manage PYQ Library</h1>
          <p className="text-gray-600">{pyqs.length} papers • {pyqs.filter(p => p.is_published).length} published</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="add-pyq-btn" onClick={startCreate} className="bg-red-600 hover:bg-red-700">
            <Plus className="mr-2" size={16} /> Add PYQ
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="mb-4 relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
        <input
          data-testid="search-pyq"
          type="text"
          placeholder="Search…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm"
        />
      </div>

      {showForm && (
        <Card className="mb-6 border-2 border-red-200">
          <CardHeader className="bg-red-50">
            <div className="flex justify-between items-center">
              <CardTitle>{editingId ? 'Edit PYQ' : 'Add New PYQ'}</CardTitle>
              <button onClick={() => setShowForm(false)} className="text-gray-500 hover:text-gray-700">
                <X size={20} />
              </button>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Exam</label>
                <select
                  data-testid="form-exam"
                  value={form.exam_id}
                  onChange={(e) => {
                    const p = EXAM_PRESETS.find(x => x.id === e.target.value);
                    setForm({ ...form, exam_id: e.target.value, exam_name: p?.name || '' });
                  }}
                  className="w-full px-3 py-2 border rounded"
                >
                  {EXAM_PRESETS.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Tier / Stage</label>
                <select
                  data-testid="form-tier"
                  value={form.tier}
                  onChange={(e) => setForm({ ...form, tier: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                >
                  <option>Tier 1</option>
                  <option>Tier 2</option>
                  <option>Prelims</option>
                  <option>Mains</option>
                  <option>Phase 1</option>
                  <option>Phase 2</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Year</label>
                <input
                  data-testid="form-year"
                  type="number"
                  value={form.year}
                  onChange={(e) => setForm({ ...form, year: parseInt(e.target.value, 10) })}
                  className="w-full px-3 py-2 border rounded"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Exam Date (optional)</label>
                <input
                  data-testid="form-date"
                  type="text"
                  placeholder="e.g. 9 Sep 2024"
                  value={form.exam_date}
                  onChange={(e) => setForm({ ...form, exam_date: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Shift (optional)</label>
                <input
                  data-testid="form-shift"
                  type="text"
                  placeholder="e.g. Shift 1"
                  value={form.shift}
                  onChange={(e) => setForm({ ...form, shift: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Language</label>
                <select
                  data-testid="form-language"
                  value={form.language}
                  onChange={(e) => setForm({ ...form, language: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                >
                  <option>English</option>
                  <option>Hindi</option>
                  <option>Bilingual</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">PDF File <span className="text-red-600">*</span></label>
                <input
                  ref={fileInputRef}
                  data-testid="form-pdf-file"
                  type="file"
                  accept="application/pdf,.pdf"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                {form.pdf_url ? (
                  <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded px-3 py-3">
                    <div className="flex items-center gap-2 text-sm text-green-800 min-w-0">
                      <Check size={16} className="flex-shrink-0" />
                      <div className="min-w-0">
                        {uploadInfo ? (
                          <>
                            Uploaded <strong>{uploadInfo.name}</strong> ({uploadInfo.size} MB)
                          </>
                        ) : (
                          <>
                            Current file: <a
                              href={form.pdf_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="underline truncate inline-block max-w-[420px] align-bottom"
                            >{form.pdf_url.split('/').pop()}</a>
                          </>
                        )}
                      </div>
                    </div>
                    <Button
                      data-testid="form-replace-pdf-btn"
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                    >
                      {uploading ? (
                        <><Loader2 className="animate-spin mr-1.5" size={14} /> Uploading…</>
                      ) : (
                        <><Upload className="mr-1.5" size={14} /> Replace</>
                      )}
                    </Button>
                  </div>
                ) : (
                  <button
                    data-testid="form-upload-btn"
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="w-full border-2 border-dashed border-red-300 hover:border-red-500 hover:bg-red-50 transition-colors rounded-lg p-6 text-center"
                  >
                    {uploading ? (
                      <div className="flex items-center justify-center gap-2 text-red-600">
                        <Loader2 className="animate-spin" size={18} /> Uploading…
                      </div>
                    ) : (
                      <>
                        <Upload className="mx-auto mb-2 text-red-500" size={28} />
                        <p className="text-sm font-medium text-gray-700">Click to upload PDF</p>
                        <p className="text-xs text-gray-500 mt-0.5">PDF files only · max 30 MB</p>
                      </>
                    )}
                  </button>
                )}
              </div>
              <div className="md:col-span-2 flex flex-wrap gap-6">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.has_answer_key}
                    onChange={(e) => setForm({ ...form, has_answer_key: e.target.checked })}
                  />
                  Has Answer Key
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.is_published}
                    onChange={(e) => setForm({ ...form, is_published: e.target.checked })}
                  />
                  Published (visible to students)
                </label>
              </div>
              <div className="md:col-span-2 flex gap-3">
                <Button data-testid="submit-pyq-btn" type="submit" disabled={submitting} className="bg-red-600 hover:bg-red-700">
                  {submitting ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save className="mr-2" size={16} />}
                  {editingId ? 'Update' : 'Create'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="text-center py-12"><Loader2 className="animate-spin mx-auto text-red-600" size={48} /></div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="px-4 py-3">Exam</th>
                <th className="px-4 py-3">Year</th>
                <th className="px-4 py-3">Tier</th>
                <th className="px-4 py-3">Date / Shift</th>
                <th className="px-4 py-3">Lang</th>
                <th className="px-4 py-3">Downloads</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => (
                <tr key={p.id} className="border-t hover:bg-gray-50" data-testid={`row-${p.id}`}>
                  <td className="px-4 py-3 font-medium">{p.exam_name}</td>
                  <td className="px-4 py-3">{p.year}</td>
                  <td className="px-4 py-3">{p.tier}</td>
                  <td className="px-4 py-3">{p.exam_date} {p.shift && `· ${p.shift}`}</td>
                  <td className="px-4 py-3"><Badge variant="outline">{p.language}</Badge></td>
                  <td className="px-4 py-3">{p.download_count}</td>
                  <td className="px-4 py-3">
                    <button
                      data-testid={`toggle-publish-${p.id}`}
                      onClick={() => togglePublish(p)}
                      className={`px-2 py-1 rounded text-xs font-medium ${p.is_published ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}
                    >
                      {p.is_published ? 'Published' : 'Draft'}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <Button data-testid={`edit-${p.id}`} variant="outline" size="sm" onClick={() => startEdit(p)}>
                        <Edit size={14} />
                      </Button>
                      <Button data-testid={`delete-${p.id}`} variant="outline" size="sm" onClick={() => handleDelete(p.id)}>
                        <Trash2 size={14} className="text-red-600" />
                      </Button>
                      <a href={p.pdf_url} target="_blank" rel="noopener noreferrer">
                        <Button variant="outline" size="sm">View</Button>
                      </a>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center py-12 text-gray-500">No PYQs match your search.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ManagePYQ;
